import React, { useContext, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  makeStyles,
  Button,
  Typography,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText, DialogActions,
} from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Alert from '@material-ui/lab/Alert';
import AppContext from '../../state/AppContext';
import { fetchStaticToken } from '../../helpers/authentication';
import api from '../../config/api';
import clientCategoryState, { clientTypeState } from '../../state';
import {
  preSelectedClinicIdState,
  selectedClinicIdState,
  selectedMedicalFileIdState,
  selectedServiceIdState,
  selectedSubserviceIdState,
  selectedTherapistState,
  selectedTreatmentIdState,
} from '../../state/selected';
import {
  clinicsState, jwtTokenState,
  servicesState,
  therapistsState,
  validatedCustomerState,
} from '../../state/apiData';

const useStyles = makeStyles((theme) => ({
  container: {
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.common.white,
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  therapistName: {
    textAlign: 'right',
    marginBottom: theme.spacing(3),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  btn: {
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  clinicLink: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    textDecoration: 'none',
  },
}));

const FutureAppointment = (props) => {
  const { futureAppointment } = props;
  const classes = useStyles();
  const intl = useIntl();
  const {
    setAppointmentToModify,
    setIsFirstAppointment,
  } = useContext(AppContext);

  const jwtToken = useRecoilValue(jwtTokenState);
  const validatedCustomer = useRecoilValue(validatedCustomerState);
  const therapists = useRecoilValue(therapistsState);
  const services = useRecoilValue(servicesState);
  const clinics = useRecoilValue(clinicsState);
  const setSelectedTreatmentId = useSetRecoilState(selectedTreatmentIdState);
  const setSelectedTherapist = useSetRecoilState(selectedTherapistState);
  const setSelectedServiceId = useSetRecoilState(selectedServiceIdState);
  const setSelectedSubserviceId = useSetRecoilState(selectedSubserviceIdState);
  const setSelectedMedicalFileId = useSetRecoilState(selectedMedicalFileIdState);
  const setSelectedClinicId = useSetRecoilState(selectedClinicIdState);
  const setClientType = useSetRecoilState(clientTypeState);
  const setClientCategory = useSetRecoilState(clientCategoryState);
  const setPreSelectedClinicId = useSetRecoilState(preSelectedClinicIdState);

  const [errorDeleteFutureAppointments, setErrorDeleteFutureAppointments] = useState(false);
  const [isDeletingFutureAppointment, setIsDeletingFutureAppointment] = useState(false);
  const [showCancelledConfirmation, setShowCancelledConfirmation] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const deleteFutureAppointment = async () => {
    setErrorDeleteFutureAppointments(false);
    setIsDeletingFutureAppointment(true);

    const responseFutureAppointment = await fetchStaticToken({
      url: api.futureAppointment,
      method: 'DELETE',
      body: {
        email: validatedCustomer.email,
        customerID: validatedCustomer.customerID,
        jwt: jwtToken,
        appointmentID: futureAppointment.id,
      },
    });

    if (responseFutureAppointment.status === 200) {
      if (window && window.gtag) {
        window.gtag('event', 'select_content', {
          content_name: 'Appointment Cancelled',
          content_lang: intl.locale,
        });
      }
      setShowCancelledConfirmation(true);
      return setIsDeletingFutureAppointment(false);
    }

    setErrorDeleteFutureAppointments(true);
    return setIsDeletingFutureAppointment(false);
  };

  const therapist = therapists ? therapists.filter(
    (t) => (parseInt(t.therapistId, 10) === parseInt(futureAppointment.therapistID, 10)
    ),
  )[0] : null;

  const service = services ? services.filter(
    (s) => (parseInt(s.externalTherapyID, 10) === parseInt(futureAppointment.disciplineID, 10)
    ),
  )[0] : null;

  const subservice = service && service.subServices ? service.subServices.filter(
    (subService) => (
      parseInt(subService.client_type2Id, 10) === parseInt(futureAppointment.subdisciplineID, 10)
    ),
  )[0] : null;

  const clinic = clinics ? clinics.filter(
    (c) => (
      parseInt(c.id, 10) === parseInt(futureAppointment.clinicID, 10)
    ),
  )[0] : null;

  const startDateDayJs = dayjs(`${futureAppointment.date} ${futureAppointment.heure}`);

  const startDate = startDateDayJs
    .locale(intl.locale)
    .format('LLLL')
    .toString();

  const endDateDayJs = dayjs(`${futureAppointment.date} ${futureAppointment.heure_fin}`);

  const endDate = endDateDayJs
    .locale(intl.locale)
    .format('LT')
    .toString();

  const isAvailableForAction = (
    dayjs(`${futureAppointment.date} ${futureAppointment.heure}`)
    > dayjs()
      .add(1, 'day')
  );

  const onModifyBtnClick = () => {
    if (window && window.gtag) {
      window.gtag('event', 'select_content', {
        content_name: 'Attempt to Modify Appointment',
        content_lang: intl.locale,
      });
    }
    setSelectedServiceId(futureAppointment.disciplineID);
    setSelectedSubserviceId(futureAppointment.subdisciplineID);
    setSelectedMedicalFileId(futureAppointment.DmedID);
    setSelectedTherapist(therapist);
    setSelectedTreatmentId(futureAppointment.treatmentID);
    setAppointmentToModify(futureAppointment);
    setIsFirstAppointment(futureAppointment.isFirstAppointment);
    setClientType(futureAppointment.client_type);
    setClientCategory(futureAppointment.client_category);
    setPreSelectedClinicId(futureAppointment.clinicID);
    return setSelectedClinicId(futureAppointment.clinicID);
  };

  if (showCancelledConfirmation) {
    return (
      <Box mb={2}>
        <Alert severity="success">
          <Typography>
            <FormattedMessage id="FutureAppointments.CancelSuccessMsg" />
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <div className={classes.container}>
      <Typography variant="body2" component="div">
        {service
          && service.therapyName[intl.locale]}
        {service && service.subServices.length > 1 && subservice
          && (
            <>
              {' / '}
              {subservice.subServiceName[intl.locale]}
            </>
          )}
      </Typography>

      <Typography component="div">
        {startDate}
        {' '}
        -
        {' '}
        {endDate}
      </Typography>
      <Typography component="div">
        {therapist && (
          <>
            <FormattedMessage id="SelectedTimeSlotDetails.specialist" />
            {therapist.firstName}
            {' '}
            {therapist.therapistName}
            {' '}
          </>
        )}
        {clinic && clinic.nom && clinic.www && (
          <Typography component="div">
            <a
              target="_blank"
              href={`https://${clinic.www}`}
              className={classes.clinicLink}
              rel="noreferrer"
            >
              {clinic.nom}
            </a>
          </Typography>
        )}
      </Typography>
      {isAvailableForAction && (
        <div
          className={classes.actions}
        >
          <Button
            className={classes.btn}
            variant="contained"
            color="primary"
            onClick={() => {
              if (window && window.gtag) {
                window.gtag('event', 'select_content', {
                  content_name: 'Attempt to Cancel Appointment',
                  content_lang: intl.locale,
                });
              }
              setConfirmationDialogOpen(true);
            }}
            disabled={isDeletingFutureAppointment}
          >
            {isDeletingFutureAppointment
              ? <CircularProgress size={20} />
              : <FormattedMessage id="FutureAppointments.cancel" />}
          </Button>

          <Dialog
            open={confirmationDialogOpen}
          >
            <DialogTitle>
              <FormattedMessage id="FutureAppointments.confirmationTitle" />
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <FormattedMessage id="FutureAppointments.confirmationText" />
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setConfirmationDialogOpen(false);
                }}
              >
                <FormattedMessage id="FutureAppointments.abort" />
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={deleteFutureAppointment}
                autoFocus
                disabled={isDeletingFutureAppointment}
              >
                {isDeletingFutureAppointment
                  ? <CircularProgress size={20} />
                  : <FormattedMessage id="FutureAppointments.confirm" />}
              </Button>
            </DialogActions>
          </Dialog>

          <Button
            className={classes.btn}
            variant="contained"
            color="primary"
            onClick={onModifyBtnClick}
          >
            <FormattedMessage id="FutureAppointments.modify" />
          </Button>

          {errorDeleteFutureAppointments
            && (
              <Box mt={3}>
                <Alert severity="error">
                  <FormattedMessage id="FollowUpAppointment.errorDelete" />
                </Alert>
              </Box>
            )}
        </div>
      )}
    </div>
  );
};

FutureAppointment.propTypes = {
  futureAppointment: PropTypes.shape({
    id: PropTypes.number,
    disciplineID: PropTypes.number,
    subdisciplineID: PropTypes.number,
    clinicID: PropTypes.number,
    therapistID: PropTypes.number,
    treatmentID: PropTypes.number,
    DmedID: PropTypes.number,
    date: PropTypes.string,
    heure: PropTypes.string,
    heure_fin: PropTypes.string,
    isFirstAppointment: PropTypes.bool,
    client_type: PropTypes.number,
    client_category: PropTypes.number,
  }).isRequired,
};

export default FutureAppointment;
